// Canvas renderer for the 9x9 sudoku board. Pure rendering — owns no state.
// The caller passes a BoardRenderState every frame; the renderer reads its
// theme + grids out of that, so settings changes apply immediately on the
// next paint without any explicit reset.

import { BOARD_COLS, BOARD_ROWS, BOX_SIZE } from '../utils/constants.js';
import type { BoardRegion } from '../layout/types.js';
import type { Digit, NonEmptyDigit } from '../game/types.js';
import type { BoardRenderState } from './types.js';

type Ctx = CanvasRenderingContext2D;

const PENCIL_GRID = 3;
const DIGIT_FONT_SCALE = 0.62;
const PENCIL_FONT_SCALE = 0.28;
const HINT_RING_INSET_PX = 3;
const HINT_RING_THICKNESS_PX = 3;
const HINT_LABEL_FONT_PX = 14;
const HINT_LABEL_PADDING_PX = 10;
const HINT_LABEL_RADIUS_PX = 8;
const HINT_LABEL_GAP_PX = 8;
const FONT_FAMILY = '"Segoe UI", system-ui, -apple-system, sans-serif';

/**
 * Returns every cell index that should share the "same-digit" dim wash
 * given the current focused digit. Pure function — exposed so tests can pin
 * down the highlight policy without faking a canvas.
 *
 * Rule: when a focused digit is set (either inferred from the selected cell
 * or latched via a numpad tap), every cell whose placed digit matches OR
 * whose pencil marks contain it gets the wash. The selected cell itself is
 * excluded from the wash set so its brighter selection fill stays distinct.
 */
export function pickHighlightedCells(state: BoardRenderState): ReadonlySet<number> {
  const result = new Set<number>();
  const { focusedDigit, selectedIndex, user, givens, pencil } = state;
  if (focusedDigit === null) return result;
  for (let i = 0; i < user.length; i += 1) {
    if (i === selectedIndex) continue;
    if (digitAt(user, givens, i) === focusedDigit) {
      result.add(i);
      continue;
    }
    if ((user[i] ?? 0) === 0 && (pencil[i]?.has(focusedDigit) ?? false)) {
      result.add(i);
    }
  }
  return result;
}

function digitAt(user: readonly Digit[], givens: readonly Digit[], i: number): Digit {
  const given = givens[i];
  if (given !== undefined && given !== 0) return given;
  return (user[i] ?? 0) as Digit;
}

export class BoardRenderer {
  draw(ctx: Ctx, state: BoardRenderState): void {
    const { board } = state.layout;
    ctx.save();
    drawBackground(ctx, board, state);
    drawCellFills(ctx, board, state);
    drawGridLines(ctx, board, state);
    drawDigits(ctx, board, state);
    drawPencilMarks(ctx, board, state);
    drawHintHighlight(ctx, board, state);
    ctx.restore();
  }
}

function drawBackground(ctx: Ctx, board: BoardRegion, state: BoardRenderState): void {
  ctx.fillStyle = state.theme.boardBackground;
  ctx.fillRect(board.x, board.y, board.width, board.height);
}

function drawCellFills(ctx: Ctx, board: BoardRegion, state: BoardRenderState): void {
  // Order matters — selection sits under the same-digit wash so the focused
  // cell still reads as "the one I'm on", and error flash sits on top of
  // both so a bad entry isn't masked.
  if (state.selectedIndex !== null) {
    ctx.fillStyle = state.theme.selectedCell;
    fillCell(ctx, board, state.selectedIndex);
  }
  const sameDigits = pickHighlightedCells(state);
  if (sameDigits.size > 0) {
    ctx.fillStyle = state.theme.sameDigitHighlight;
    for (const idx of sameDigits) fillCell(ctx, board, idx);
  }
  if (state.errorFlash.size > 0) {
    ctx.fillStyle = state.theme.errorFlash;
    for (const idx of state.errorFlash) fillCell(ctx, board, idx);
  }
}

function drawGridLines(ctx: Ctx, board: BoardRegion, state: BoardRenderState): void {
  const { theme } = state;
  const { x, y, width, height, cellSize, gridLineThin, gridLineThick } = board;

  // Thin lines first, then thick on top so 3x3 borders win at intersections.
  ctx.fillStyle = theme.gridLineThin;
  for (let i = 0; i <= BOARD_COLS; i += 1) {
    if (i % BOX_SIZE === 0) continue;
    const lx = Math.round(x + i * cellSize - gridLineThin / 2);
    ctx.fillRect(lx, y, gridLineThin, height);
  }
  for (let i = 0; i <= BOARD_ROWS; i += 1) {
    if (i % BOX_SIZE === 0) continue;
    const ly = Math.round(y + i * cellSize - gridLineThin / 2);
    ctx.fillRect(x, ly, width, gridLineThin);
  }
  ctx.fillStyle = theme.gridLineThick;
  for (let i = 0; i <= BOARD_COLS; i += BOX_SIZE) {
    const lx = Math.round(x + i * cellSize - gridLineThick / 2);
    ctx.fillRect(lx, y, gridLineThick, height);
  }
  for (let i = 0; i <= BOARD_ROWS; i += BOX_SIZE) {
    const ly = Math.round(y + i * cellSize - gridLineThick / 2);
    ctx.fillRect(x, ly, width, gridLineThick);
  }
}

function drawDigits(ctx: Ctx, board: BoardRegion, state: BoardRenderState): void {
  const { theme, givens, user, solution, highlightErrors } = state;
  const fontPx = Math.floor(board.cellSize * DIGIT_FONT_SCALE);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (let i = 0; i < BOARD_ROWS * BOARD_COLS; i += 1) {
    const given = givens[i] ?? 0;
    if (given !== 0) {
      ctx.fillStyle = theme.givenDigit;
      ctx.font = `700 ${fontPx}px ${FONT_FAMILY}`;
      drawCellText(ctx, board, i, String(given));
      continue;
    }
    const entry = user[i] ?? 0;
    if (entry === 0) continue;
    const isError = highlightErrors && entry !== (solution[i] ?? 0);
    ctx.fillStyle = isError ? theme.errorFlash : theme.userDigit;
    ctx.font = `500 ${fontPx}px ${FONT_FAMILY}`;
    drawCellText(ctx, board, i, String(entry));
  }
}

function drawPencilMarks(ctx: Ctx, board: BoardRegion, state: BoardRenderState): void {
  const { theme, givens, user, pencil, focusedDigit } = state;
  const fontPx = Math.floor(board.cellSize * PENCIL_FONT_SCALE);
  ctx.font = `500 ${fontPx}px ${FONT_FAMILY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (let i = 0; i < BOARD_ROWS * BOARD_COLS; i += 1) {
    if ((givens[i] ?? 0) !== 0) continue;
    if ((user[i] ?? 0) !== 0) continue;
    const marks = pencil[i];
    if (marks === undefined || marks.size === 0) continue;
    drawPencilCell(ctx, board, i, marks, theme, focusedDigit);
  }
}

function fillCell(ctx: Ctx, board: BoardRegion, index: number): void {
  const row = Math.floor(index / BOARD_COLS);
  const col = index % BOARD_COLS;
  ctx.fillRect(
    board.x + col * board.cellSize,
    board.y + row * board.cellSize,
    board.cellSize,
    board.cellSize,
  );
}

function drawCellText(ctx: Ctx, board: BoardRegion, index: number, text: string): void {
  const row = Math.floor(index / BOARD_COLS);
  const col = index % BOARD_COLS;
  const cx = board.x + col * board.cellSize + board.cellSize / 2;
  const cy = board.y + row * board.cellSize + board.cellSize / 2;
  ctx.fillText(text, cx, cy);
}

function drawPencilCell(
  ctx: Ctx,
  board: BoardRegion,
  index: number,
  marks: ReadonlySet<NonEmptyDigit>,
  theme: BoardRenderState['theme'],
  focusedDigit: NonEmptyDigit | null,
): void {
  const row = Math.floor(index / BOARD_COLS);
  const col = index % BOARD_COLS;
  const x0 = board.x + col * board.cellSize;
  const y0 = board.y + row * board.cellSize;
  const sub = board.cellSize / PENCIL_GRID;
  for (const digit of marks) {
    const r = Math.floor((digit - 1) / PENCIL_GRID);
    const c = (digit - 1) % PENCIL_GRID;
    ctx.fillStyle = digit === focusedDigit ? theme.numpadButtonActive : theme.pencilMark;
    ctx.fillText(String(digit), x0 + c * sub + sub / 2, y0 + r * sub + sub / 2);
  }
}

function drawHintHighlight(ctx: Ctx, board: BoardRegion, state: BoardRenderState): void {
  const { activeHint, theme } = state;
  if (activeHint === null) return;
  ctx.save();
  ctx.strokeStyle = theme.numpadButtonActive;
  ctx.lineWidth = HINT_RING_THICKNESS_PX;
  ctx.lineJoin = 'round';
  for (const index of activeHint.cells) {
    const row = Math.floor(index / BOARD_COLS);
    const col = index % BOARD_COLS;
    const x = board.x + col * board.cellSize + HINT_RING_INSET_PX;
    const y = board.y + row * board.cellSize + HINT_RING_INSET_PX;
    const size = board.cellSize - HINT_RING_INSET_PX * 2;
    ctx.strokeRect(x, y, size, size);
  }
  drawHintLabel(ctx, board, activeHint.label, theme);
  ctx.restore();
}

function drawHintLabel(ctx: Ctx, board: BoardRegion, label: string, theme: BoardRenderState['theme']): void {
  ctx.font = `600 ${HINT_LABEL_FONT_PX}px ${FONT_FAMILY}`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  const metrics = ctx.measureText(label);
  const w = Math.ceil(metrics.width) + HINT_LABEL_PADDING_PX * 2;
  const h = HINT_LABEL_FONT_PX + HINT_LABEL_PADDING_PX;
  const cx = board.x + board.width / 2;
  const cy = board.y + board.height + HINT_LABEL_GAP_PX + h / 2;
  const x = cx - w / 2;
  const y = cy - h / 2;
  ctx.fillStyle = theme.numpadButtonActive;
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, w, h, HINT_LABEL_RADIUS_PX);
  else ctx.rect(x, y, w, h);
  ctx.fill();
  ctx.fillStyle = theme.numpadDigit;
  ctx.fillText(label, cx, cy);
}
